import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Input,
  Label,
} from "reactstrap";

import { useFetchLocationsQuery } from "../api/cmsPanelsApi";
import { getApiErrorMessage } from "../api/errorUtils";
import {
  useFetchSaleByIdQuery,
  useFetchSalesListQuery,
  useLazyFetchSalesListQuery,
} from "../api/salesApi";
import {
  printDailySalesInvoice,
  printSaleReceipt,
} from "../components/Print/commerceDocumentPrint";
import { useTranslation } from "../hooks/useTranslation";
import {
  buildSaleItemDisplayLabel,
  classifyOptionSegment,
  formatMoney,
  formatSaleDateTime,
  resolvePaymentMethodLabel,
  todayString,
  useDebounce,
} from "./salesUtils";

const RecentSalesPage = () => {
  const t = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(
    String(searchParams.get("date") || "").trim() || todayString()
  );
  const [locationId, setLocationId] = useState(
    String(searchParams.get("locationId") || "").trim()
  );
  const [saleNumberInput, setSaleNumberInput] = useState(
    String(searchParams.get("saleNumber") || "").trim()
  );
  const [selectedSaleId, setSelectedSaleId] = useState(() => {
    const value = Number(searchParams.get("saleId"));
    return Number.isInteger(value) && value > 0 ? value : null;
  });

  const debouncedSaleNumber = useDebounce(saleNumberInput, 350);

  const { data: locations = [], isFetching: isFetchingLocations } =
    useFetchLocationsQuery();
  const {
    data: salesList,
    isFetching: isFetchingSales,
    error: salesListError,
  } = useFetchSalesListQuery({
    date: selectedDate,
    locationId: locationId || undefined,
    saleNumber: debouncedSaleNumber || undefined,
    pageSize: 30,
  });
  const [fetchSalesForPrint, { isFetching: isFetchingPrintList }] =
    useLazyFetchSalesListQuery();
  const {
    data: selectedSale,
    isFetching: isFetchingSaleDetail,
    error: saleDetailError,
  } = useFetchSaleByIdQuery(selectedSaleId, {
    skip: !selectedSaleId,
  });

  useEffect(() => {
    if (!locationId && locations.length) {
      setLocationId("");
    }
  }, [locationId, locations]);

  useEffect(() => {
    const nextParams = new URLSearchParams();

    if (selectedDate) nextParams.set("date", selectedDate);
    if (locationId) nextParams.set("locationId", locationId);
    if (debouncedSaleNumber) nextParams.set("saleNumber", debouncedSaleNumber);
    if (selectedSaleId) nextParams.set("saleId", String(selectedSaleId));

    const nextQuery = nextParams.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [
    locationId,
    debouncedSaleNumber,
    searchParams,
    selectedDate,
    selectedSaleId,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!salesList?.items?.length) {
      setSelectedSaleId(null);
      return;
    }
    if (selectedSaleId) {
      const stillVisible = salesList.items.some((item) => item.id === selectedSaleId);
      if (!stillVisible) {
        setSelectedSaleId(null);
      }
    }
  }, [salesList, selectedSaleId]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && selectedSaleId) setSelectedSaleId(null);
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [selectedSaleId]);

  const selectedSaleSummary = useMemo(
    () => salesList?.items?.find((item) => item.id === selectedSaleId) || null,
    [salesList, selectedSaleId]
  );

  const drawerOpen = Boolean(selectedSaleId);

  const locationLabel = useMemo(() => {
    if (!locationId) return "";
    const loc = locations.find((l) => String(l.id) === String(locationId));
    return loc?.name || "";
  }, [locationId, locations]);

  const handlePrintDayInvoice = async () => {
    try {
      const result = await fetchSalesForPrint({
        date: selectedDate,
        locationId: locationId || undefined,
        saleNumber: debouncedSaleNumber || undefined,
        pageSize: 500,
      }).unwrap();
      const items = Array.isArray(result?.items) ? result.items : [];
      if (!items.length) {
        return;
      }
      printDailySalesInvoice({
        date: selectedDate,
        locationLabel: locationLabel || "",
        saleNumberFilter: debouncedSaleNumber || "",
        sales: items,
      });
    } catch (e) {
      console.error("Print day invoice failed:", e);
    }
  };

  const clearFilters = () => {
    setSelectedDate(todayString());
    setLocationId("");
    setSaleNumberInput("");
  };

  const hasActiveFilters =
    Boolean(locationId) ||
    Boolean(saleNumberInput.trim()) ||
    selectedDate !== todayString();

  // sale.lineCount is the number of distinct sale lines (products), not the
  // total quantity of units sold — the list payload only loads the first two
  // lines' quantities, so "X artikuj" would misreport a single line sold at
  // quantity 2 as "1 artikull". Label it as products instead.
  const formatProductCount = (count) =>
    Number(count) === 1
      ? t("recentSales.productCount").replace("{count}", count)
      : t("recentSales.productCountPlural").replace("{count}", count);

  // Same option-dimension normalization already established on the Sales page,
  // reused via the shared salesUtils classifier rather than duplicated parsing.
  const buildOptionChips = (optionSummary) =>
    String(optionSummary || "")
      .split(" / ")
      .map((segment) => classifyOptionSegment(segment))
      .filter(Boolean)
      .map((option) => {
        if (option.kind === "color") {
          return t("commerceCommon.optionColorLabel").replace("{value}", option.value);
        }
        if (option.kind === "size") {
          return t("commerceCommon.optionSizeLabel").replace("{value}", option.value);
        }
        return option.name ? `${option.name}: ${option.value}` : option.value;
      });

  const totalCount = salesList?.total ?? 0;
  const loadedSales = salesList?.items || [];
  // The API returns only the current page (capped server-side) and no aggregate
  // sum. Only trust a money metric when the loaded page provably contains every
  // matching sale — otherwise a partial-page sum would misrepresent the filter.
  const hasCompleteResultSet = loadedSales.length > 0 && loadedSales.length >= totalCount;
  const totalValue = hasCompleteResultSet
    ? loadedSales.reduce((sum, sale) => sum + Number(sale.totalAmount || 0), 0)
    : null;
  const averageValue =
    totalValue !== null && totalCount > 0 ? totalValue / totalCount : null;
  // Net = what was sold minus refunds on those same sales, whenever the
  // return happened, so a returned item no longer counts as sold.
  const returnedValue = hasCompleteResultSet
    ? loadedSales.reduce((sum, sale) => sum + Number(sale.returnedAmount || 0), 0)
    : null;
  const netValue =
    totalValue !== null ? Math.max(0, totalValue - (returnedValue || 0)) : null;

  return (
    <div className="content sales-premium sales-page recent-sales-page">
      <Card className="sales-page-card">
        <CardHeader className="sales-page-header">
          <div>
            <div className="sp-title">{t("recentSales.title")}</div>
            <p className="sp-subtitle mb-0">{t("recentSales.subtitle")}</p>
          </div>
          <div className="sales-page-header__actions">
            <Button
              color="secondary"
              type="button"
              className="rs-print-btn"
              disabled={
                isFetchingSales ||
                isFetchingPrintList ||
                !(salesList?.items?.length > 0)
              }
              onClick={handlePrintDayInvoice}
            >
              <i className="tim-icons icon-paper" aria-hidden="true" />
              {isFetchingPrintList
                ? t("commerceCommon.preparing")
                : t("recentSales.printDayInvoice")}
            </Button>
          </div>
        </CardHeader>
        <CardBody className="sales-page-body">
          {salesListError && (
            <Alert color="danger" className="sp-feedback-alert">
              {getApiErrorMessage(salesListError, t("recentSales.loadError"))}
            </Alert>
          )}

          <section className="rs-filters">
            <FormGroup className="rs-filter-field">
              <Label for="recent-sales-date">{t("commerceCommon.date")}</Label>
              <Input
                id="recent-sales-date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                disabled={isFetchingSales}
              />
            </FormGroup>
            <FormGroup className="rs-filter-field">
              <Label for="recent-sales-location">{t("commerceCommon.location")}</Label>
              <Input
                id="recent-sales-location"
                type="select"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                disabled={isFetchingLocations || isFetchingSales}
              >
                <option value="">{t("commerceCommon.allLocations")}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.name ||
                      t("commerceCommon.locationFallback").replace("{id}", loc.id)}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <FormGroup className="rs-filter-field rs-filter-field--search">
              <Label for="recent-sales-number">{t("commerceCommon.saleNumber")}</Label>
              <div className="rs-search-wrap">
                <i className="tim-icons icon-zoom-split rs-search-icon" aria-hidden="true" />
                <Input
                  id="recent-sales-number"
                  type="text"
                  autoComplete="off"
                  className="rs-search-input"
                  placeholder={t("recentSales.searchPlaceholder")}
                  value={saleNumberInput}
                  onChange={(e) => setSaleNumberInput(e.target.value)}
                />
              </div>
            </FormGroup>
            {hasActiveFilters && (
              <button type="button" className="rs-clear-filters" onClick={clearFilters}>
                <i className="tim-icons icon-simple-remove" aria-hidden="true" />
                {t("recentSales.clearFilters")}
              </button>
            )}
          </section>

          {totalCount > 0 && (
            <div className="rs-summary-cards">
              <div className="rs-metric-card">
                <span className="rs-metric-icon">
                  <i className="tim-icons icon-bag-16" aria-hidden="true" />
                </span>
                <span className="rs-metric-body">
                  <span className="rs-metric-value">{totalCount}</span>
                  <span className="rs-metric-label">
                    {t("recentSales.salesCountLabel")}
                  </span>
                </span>
              </div>
              {totalValue !== null && (
                <div className="rs-metric-card">
                  <span className="rs-metric-icon">
                    <i className="tim-icons icon-coins" aria-hidden="true" />
                  </span>
                  <span className="rs-metric-body">
                    <span className="rs-metric-value">{formatMoney(totalValue)}</span>
                    <span className="rs-metric-label">
                      {t("recentSales.totalValueLabel")}
                    </span>
                  </span>
                </div>
              )}
              {netValue !== null && (
                <div className="rs-metric-card rs-metric-card--net">
                  <span className="rs-metric-icon">
                    <i className="tim-icons icon-money-coins" aria-hidden="true" />
                  </span>
                  <span className="rs-metric-body">
                    <span className="rs-metric-value">{formatMoney(netValue)}</span>
                    <span className="rs-metric-label">
                      {t("recentSales.netValueLabel")}
                    </span>
                    {returnedValue > 0 && (
                      <span className="rs-metric-hint">
                        {t("recentSales.netValueReturnedHint").replace(
                          "{amount}",
                          formatMoney(returnedValue)
                        )}
                      </span>
                    )}
                  </span>
                </div>
              )}
              {averageValue !== null && (
                <div className="rs-metric-card">
                  <span className="rs-metric-icon">
                    <i className="tim-icons icon-chart-bar-32" aria-hidden="true" />
                  </span>
                  <span className="rs-metric-body">
                    <span className="rs-metric-value">{formatMoney(averageValue)}</span>
                    <span className="rs-metric-label">
                      {t("recentSales.averageLabel")}
                    </span>
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="rs-table">
            <div className="rs-table-row rs-table-row--header" aria-hidden="true">
              <span>{t("commerceCommon.number")}</span>
              <span className="rs-col-datetime">{t("storeSales.dateAndTime")}</span>
              <span>{t("recentSales.productsColumnHeader")}</span>
              <span className="rs-col-location">{t("commerceCommon.location")}</span>
              <span className="rs-col-payment">{t("commerceCommon.payment")}</span>
              <span>{t("commerceCommon.total")}</span>
              <span />
            </div>

            {isFetchingSales ? (
              <div className="rs-table-status">
                <span>{t("commerceCommon.loading")}</span>
              </div>
            ) : loadedSales.length ? (
              <div className="rs-table-body">
                {loadedSales.map((sale) => (
                  <button
                    key={sale.id}
                    type="button"
                    className={`rs-table-row${
                      selectedSaleId === sale.id ? " is-selected" : ""
                    }`}
                    onClick={() => setSelectedSaleId(sale.id)}
                  >
                    <span className="rs-cell-number">{sale.saleNumber}</span>
                    <span className="rs-cell-datetime">
                      {formatSaleDateTime(sale.completedAt)}
                    </span>
                    <div className="rs-cell-items">
                      <span className="rs-cell-items__count">
                        {formatProductCount(sale.lineCount)}
                      </span>
                      {sale.itemSummary && (
                        <span className="rs-cell-items__summary sp-truncate">
                          {sale.itemSummary}
                        </span>
                      )}
                      {sale.returnedAmount > 0 && (
                        <span className="rs-returned-badge">
                          {t("recentSales.saleHasReturnBadge")}
                        </span>
                      )}
                    </div>
                    <span className="rs-cell-location sp-truncate">
                      {sale.location?.name || "—"}
                    </span>
                    <span className="rs-cell-payment">
                      {resolvePaymentMethodLabel(sale.paymentMethod, t)}
                    </span>
                    <span className="rs-cell-total">{formatMoney(sale.totalAmount)}</span>
                    <i
                      className="tim-icons icon-minimal-right rs-cell-chevron"
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
            ) : (
              <div className="rs-table-status">
                <span>{t("recentSales.noSalesFound")}</span>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* ── Drawer backdrop ── */}
      <div
        className={`sp-drawer-backdrop${drawerOpen ? " open" : ""}`}
        onClick={() => setSelectedSaleId(null)}
      />

      {/* ── Drawer panel ── */}
      <div className={`sp-drawer rs-drawer${drawerOpen ? " open" : ""}`}>
        <div className="sp-drawer-header rs-drawer-header">
          <div>
            <div className="sp-title">{t("commerceCommon.saleDetail")}</div>
            <p className="rs-drawer-sale-number">
              {selectedSale?.saleNumber || selectedSaleSummary?.saleNumber || ""}
            </p>
          </div>
          <button
            type="button"
            className="sp-drawer-close"
            onClick={() => setSelectedSaleId(null)}
            aria-label={t("commerceCommon.close")}
          >
            ×
          </button>
        </div>

        <div className="sp-drawer-body">
          {saleDetailError ? (
            <Alert color="danger">
              {getApiErrorMessage(saleDetailError, t("recentSales.detailLoadError"))}
            </Alert>
          ) : isFetchingSaleDetail ? (
            <span className="sp-text-sm sp-text-muted">{t("commerceCommon.loading")}</span>
          ) : selectedSale ? (
            <>
              {selectedSale.status && (
                <div
                  className={`rs-status-banner rs-status-banner--${
                    selectedSale.status === "voided" ? "voided" : "completed"
                  }`}
                >
                  <i
                    className={`tim-icons ${
                      selectedSale.status === "voided"
                        ? "icon-alert-circle-exc"
                        : "icon-check-2"
                    }`}
                    aria-hidden="true"
                  />
                  <span className="rs-status-banner__text">
                    <span>
                      {selectedSale.status === "voided"
                        ? t("recentSales.saleStatusVoided")
                        : t("recentSales.saleStatusCompleted")}
                    </span>
                    {selectedSale.completedAt && (
                      <span className="rs-status-banner__time">
                        {formatSaleDateTime(selectedSale.completedAt)}
                      </span>
                    )}
                  </span>
                </div>
              )}

              <div className="rs-fact-grid">
                {selectedSale.location?.name && (
                  <div className="rs-fact">
                    <span className="rs-fact__icon">
                      <i className="tim-icons icon-square-pin" aria-hidden="true" />
                    </span>
                    <span className="rs-fact__body">
                      <span className="rs-fact__label">{t("commerceCommon.location")}</span>
                      <span className="rs-fact__value">{selectedSale.location.name}</span>
                    </span>
                  </div>
                )}
                <div className="rs-fact">
                  <span className="rs-fact__icon">
                    <i className="tim-icons icon-credit-card" aria-hidden="true" />
                  </span>
                  <span className="rs-fact__body">
                    <span className="rs-fact__label">{t("commerceCommon.payment")}</span>
                    <span className="rs-fact__value">
                      {resolvePaymentMethodLabel(selectedSale.paymentMethod, t)}
                    </span>
                  </span>
                </div>
              </div>

              <div className="rs-totals-card">
                <div className="rs-totals-row">
                  <span>{t("commerceCommon.subtotal")}</span>
                  <span>{formatMoney(selectedSale.subtotalAmount)}</span>
                </div>
                <div className="rs-totals-row">
                  <span>{t("commerceCommon.discount")}</span>
                  <span
                    className={
                      selectedSale.discountTotalAmount > 0 ? "sp-discount" : undefined
                    }
                  >
                    {selectedSale.discountTotalAmount > 0
                      ? `−${formatMoney(selectedSale.discountTotalAmount)}`
                      : formatMoney(0)}
                  </span>
                </div>
                <div className="rs-totals-row rs-totals-row--grand">
                  <span>{t("commerceCommon.total")}</span>
                  <span>{formatMoney(selectedSale.totalAmount)}</span>
                </div>
                {selectedSale.returnedAmount > 0 && (
                  <>
                    <div className="rs-totals-row rs-totals-row--returned">
                      <span>{t("recentSales.returnedAmountLabel")}</span>
                      <span>−{formatMoney(selectedSale.returnedAmount)}</span>
                    </div>
                    <div className="rs-totals-row rs-totals-row--net">
                      <span>{t("recentSales.netTotalLabel")}</span>
                      <span>{formatMoney(selectedSale.netAmount)}</span>
                    </div>
                  </>
                )}
              </div>

              <h3 className="rs-items-title">{t("storeSales.soldItems")}</h3>
              <div className="rs-items-table">
                <div className="rs-items-row rs-items-row--header" aria-hidden="true">
                  <span>{t("commerceCommon.itemHeader")}</span>
                  <span>{t("commerceCommon.price")}</span>
                  <span>{t("commerceCommon.qty")}</span>
                  <span>{t("commerceCommon.total")}</span>
                </div>
                {(selectedSale.lines || []).map((line) => {
                  const lineTitle =
                    line.productTitle ||
                    buildSaleItemDisplayLabel({
                      productTitle: line.productTitle,
                      variantTitle: line.variantTitle,
                      optionSummary: line.optionSummary,
                    });
                  const lineChips = buildOptionChips(line.optionSummary);
                  const returnedQuantity = Number(line.returnedQuantity || 0);

                  return (
                    <div
                      className={`rs-items-row${
                        returnedQuantity >= line.quantity && returnedQuantity > 0
                          ? " is-returned"
                          : ""
                      }`}
                      key={line.id}
                    >
                      <div className="rs-items-row__item">
                        <span className="rs-item-title">{lineTitle}</span>
                        {returnedQuantity > 0 && (
                          <span className="rs-returned-badge">
                            <i className="tim-icons icon-refresh-01" aria-hidden="true" />
                            {returnedQuantity >= line.quantity
                              ? t("recentSales.returnedBadge")
                              : t("recentSales.partiallyReturnedBadge")
                                  .replace("{returned}", returnedQuantity)
                                  .replace("{quantity}", line.quantity)}
                          </span>
                        )}
                        {line.sku && (
                          <span className="rs-item-sku sp-truncate">{line.sku}</span>
                        )}
                        {(lineChips.length > 0 || line.brandName) && (
                          <div className="rs-item-chips">
                            {line.brandName && (
                              <span className="rs-item-chip">{line.brandName}</span>
                            )}
                            {lineChips.map((chip) => (
                              <span className="rs-item-chip" key={chip}>
                                {chip}
                              </span>
                            ))}
                          </div>
                        )}
                        {line.discountTotalAmount > 0 && (
                          <span className="rs-item-was">
                            {t("commerceCommon.wasPrice").replace(
                              "{amount}",
                              formatMoney(line.unitPriceAmount)
                            )}
                          </span>
                        )}
                      </div>
                      <span className="rs-items-row__price">
                        {formatMoney(line.finalUnitPriceAmount)}
                      </span>
                      <span className="rs-items-row__qty">{line.quantity}</span>
                      <span className="rs-items-row__total">
                        {formatMoney(line.lineSubtotalAmount)}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="rs-drawer-actions">
                <Button
                  tag={Link}
                  color="success"
                  className="rs-action-btn rs-action-btn--primary"
                  to={`/admin/returns?saleNumber=${encodeURIComponent(
                    selectedSale.saleNumber
                  )}`}
                >
                  <i className="tim-icons icon-refresh-01" aria-hidden="true" />
                  <span className="rs-action-btn__text">
                    <span className="rs-action-btn__title">
                      {t("commerceCommon.startReturn")}
                    </span>
                    <span className="rs-action-btn__hint">
                      {t("recentSales.startReturnHint")}
                    </span>
                  </span>
                </Button>
                <Button
                  color="secondary"
                  className="rs-action-btn"
                  onClick={() => printSaleReceipt(selectedSale)}
                >
                  <i className="tim-icons icon-paper" aria-hidden="true" />
                  <span className="rs-action-btn__text">
                    <span className="rs-action-btn__title">
                      {t("commerceCommon.printReceipt")}
                    </span>
                    <span className="rs-action-btn__hint">
                      {t("recentSales.printReceiptHint")}
                    </span>
                  </span>
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default RecentSalesPage;
